import { booleanAttribute } from "#ce/utils/props";
export const positionOptions = ["center", "bottom", "left", "right"];
export const drawerPositions = ["left", "right"];
export const defaultWidth = "300px";

// We had bugs in the past, where the props were mutatated. Apply Object.freeze() to prevent this.
export const props = Object.freeze({
  /**
   * Accessible name for the dialog. A modal dialog is announced when it opens, and an
   * `aria-labelledby` IDREF cannot reach a title rendered outside this shadow root, so a consumer
   * behind a shadow boundary passes the name as a string.
   *
   * @type {String}
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * Whether the dialog is open.
   *
   * @type {Boolean|String}
   * @default false
   */
  open: booleanAttribute(),
  /**
   * Suppress focus restoration on close when the caller owns the next focus target.
   *
   * @type {Boolean|String}
   * @default false
   */
  noRestoreFocus: booleanAttribute(),
  /**
   * Dialog position on the screen. "center" centres the dialog; "bottom" anchors it to the bottom as a sheet; "left" and "right" dock it to that edge over the full viewport height as a drawer.
   *
   * @type {String}
   * @default "center"
   * @values center, bottom, left, right
   */
  position: {
    type: String,
    default: "center",
    validator: (value) => positionOptions.includes(value),
  },
  /**
   * Hide the backdrop.
   *
   * @type {Boolean|String}
   * @default false
   */
  noBackdrop: booleanAttribute(),
  /**
   * Disable closing the dialog when the Esc key is pressed.
   *
   * @type {Boolean|String}
   * @default false
   */
  ignoreEscapeKey: booleanAttribute(),
  /**
   * Disable closing the dialog when a pointer is pressed outside it. Esc still closes it. A bottom sheet with nothing in its `action` slot has no visible way out on a touch device.
   *
   * @type {Boolean|String}
   * @default false
   */
  ignoreOutsideClick: booleanAttribute(),
  /**
   * Width of the dialog. A drawer left at the default takes its width from `--sp-comp-dialog-drawer-width` instead.
   *
   * @type {String}
   * @default "300px"
   */
  width: {
    type: String,
    default: defaultWidth,
  },
  /**
   * Maximum width of the dialog.
   *
   * @type {String}
   * @default "none"
   */
  maxWidth: {
    type: String,
    default: "none",
  },
  /**
   * Height of the dialog.
   *
   * @type {String}
   * @default "fit-content"
   */
  height: {
    type: String,
    default: "fit-content",
  },
  /**
   * Maximum height of the dialog.
   *
   * @type {String}
   * @default "none"
   */
  maxHeight: {
    type: String,
    default: "none",
  },
  /**
   * Prevent the dialog from being dismissed by clicking outside it or pressing Esc.
   *
   * @type {Boolean|String}
   * @default false
   */
  persistent: booleanAttribute(),
  /**
   * Render the dialog fullscreen.
   *
   * @type {Boolean|String}
   * @default false
   */
  fullscreen: booleanAttribute(),
  /**
   * Clip overflowing content instead of letting the dialog body scroll. By default the body scrolls when its content overflows the available height; set this to clip the overflow instead.
   *
   * @type {Boolean|String}
   * @default false
   */
  noScrollable: booleanAttribute(),

  /**
   * Hide the dialog temporarily without firing close events. Used when a non-native modal opens above the dialog and the host needs to suppress this dialog's visibility for the duration.
   *
   * @type {Boolean|String}
   * @default false
   */
  isHiddenTemporarily: booleanAttribute(),
  /**
   * Lock page scroll while the dialog is open. When unset, defaults to `true`
   * for modal dialogs (those with a backdrop) and `false` for `no-backdrop`
   * dialogs (which are typically used as popovers and should not block the
   * page). Set explicitly to override the default behaviour.
   *
   * @type {Boolean|String}
   * @default !noBackdrop
   */
  lockScroll: booleanAttribute(undefined),
  /**
   * Animation duration for dialog open/close transitions.
   *
   * @type {String}
   * @default "0.2s"
   */
  animationTime: {
    type: String,
    default: "0.2s",
  },
});

/**
 * The resolved sp-dialog props as seen inside the component and the composables
 * it delegates to.
 *
 * @typedef {import("vue").ExtractPropTypes<typeof props>} SpDialogProps
 */
